/* eslint-disable @typescript-eslint/no-explicit-any */
import { FIND_ASSIGNABLE_FILE_ADDONS } from '@gripp/shared-logic';
import { BaseColumnCheckbox, InfiniteDataGrid, Colors } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Platform, StyleSheet, Text } from 'react-native';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EditFileAddonsListProps {
  workspaceId: string;
  fileId: string;
  maxHeight?: number;
  keyword?: string;
  assignments: string[];
  updateAssignments: (assignments: string[]) => void;
}

export const EditFileAddonsList = ({
  workspaceId,
  fileId,
  maxHeight,
  keyword,
  assignments,
  updateAssignments,
}: EditFileAddonsListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>();

  const columnHelper = createColumnHelper<any>();
  const columns: ColumnDef<any, any>[] = useMemo(
    () => [
      columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <Text style={[styles.cellText, styles.nameLabel]}>
            {info.getValue()}
          </Text>
        ),
        header: () => t('addOn.list.name'),
        meta: { style: styles.cell },
      }),
    ],
    [t]
  );

  useEffect(() => {
    setFilter({
      keyword: keyword || undefined,
      mediaId: fileId,
      assignments,
      workspaceId,
    });
  }, [workspaceId, fileId, keyword]);

  if (!filter) return null;

  return (
    <InfiniteDataGrid
      modelName="addon"
      hideResultsLabel={true}
      query={FIND_ASSIGNABLE_FILE_ADDONS}
      filter={filter}
      orderBy={{ field: 'name', order: 'ASC' as any }}
      pageSize={100}
      columns={columns}
      listMaxHeight={maxHeight as number}
      containerStyle={{ height: maxHeight }}
      paddingBottom={0}
      textEmpty={t('addOn.textEmpty')}
      dataPrefix="media.findAssignableAddonsForFile"
      updateRowsSelected={(rows) => updateAssignments(rows)}
      isRowSelected={(row) => assignments.includes(row.id)}
      enableScrollRefresh
      showScrollIndicator
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: 4,
    ...Platform.select({
      web: { height: 50, paddingRight: 16 },
      default: { height: 68, paddingRight: 12 },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: { color: Colors.black },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: { fontWeight: '500', flexWrap: 'nowrap' },
      default: { fontWeight: '400', flexWrap: 'wrap' },
    }),
  },
});
